import { useRef, useMemo } from 'react';
import { useField, useForm, SubmitResult } from '@shopify/react-form';
import getErrorForToast from '@sdk/api/utils/getErrorForToast';
import useSlingApi from '@sdk/api/useSlingApi';
import { isUnscheduledShift } from '@sdk/automatedBreaks/utils';

import { FormSubmitStatuses } from '@web/forms/types';
import {
  UseEditTimesheetFormProps,
  EditFormFieldValues,
} from '@web/timesheets/EditTimesheetModal/editTimesheetModal.types';
import { decorateSubmit } from '@web/forms/utils/decorateSubmit';
import {
  getFieldValues,
  filterUnmodifiedEntires,
  deleteDanglingStatusChangeEntries,
  getShiftPayload,
} from '@web/timesheets/EditTimesheetModal/editTimesheetModal.utils';
import { validateEditTimesheetForm } from '@web/timesheets/EditTimesheetModal/utils/validation';
import { mapTimesheetFormFieldsToBulkPayload } from '@web/timesheets/EditTimesheetModal/utils/mappers';
import useRequireAssigneeConsent from '@web/timesheets/EditTimesheetModal/hooks/useRequireAssigneeConsent';
import useTimeClockSection from '@web/timesheets/EditTimesheetModal/hooks/useTimeClockSection';
import { markInvalidTimeClockEntries } from '@web/timesheets/CreateTimesheetModal/utils/timeClockUtils';

export const useEditTimesheetModalForm = ({ shift }: UseEditTimesheetFormProps) => {
  // a workaround to let ProgressButton work with form submit handler
  const submitPromise = useRef<Promise<SubmitResult>>();
  const api = useSlingApi();
  const dateField = useField<Date>(shift.dtstart);
  const timeClockSection = useTimeClockSection(shift.timesheetEntries);
  const requireAssigneeConsent = useRequireAssigneeConsent();

  const formData = useForm({
    fields: {
      date: dateField,
    },
    dynamicLists: {
      timeClock: timeClockSection,
    },
    onSubmit: async (fieldValues: EditFormFieldValues) => {
      const formErrors = validateEditTimesheetForm(fieldValues);

      if (Object.keys(formErrors).length) {
        markInvalidTimeClockEntries({ formErrors, timeClockFields: timeClockSection.fields });

        submitPromise.current = Promise.reject({
          status: FormSubmitStatuses.fail,
          errors: {
            formErrors,
          },
        });
        return submitPromise.current;
      }

      const rawPayload = mapTimesheetFormFieldsToBulkPayload(fieldValues, shift);
      const filteredPayload = filterUnmodifiedEntires(rawPayload, shift.timesheetEntries);
      const finalPayload = deleteDanglingStatusChangeEntries(
        filteredPayload,
        shift.timesheetEntries,
        shift.timesheetProjections
      );

      try {
        if (filteredPayload.length) {
          await api.timesheets.bulkUpdate(finalPayload, requireAssigneeConsent);

          // Update dtstart/dtend on unscheduled shift during editing the date on the timesheet edit modal.
          if (dateField.dirty && isUnscheduledShift(shift)) {
            const shiftPayload = getShiftPayload(shift, rawPayload);

            await api.shifts.updateShift(shift.id, shiftPayload, {
              ignoreConflicts: true,
            });
          }
        }
        submitPromise.current = Promise.resolve({ status: FormSubmitStatuses.success });
        return submitPromise.current;
      } catch (error) {
        submitPromise.current = Promise.reject({
          status: FormSubmitStatuses.fail,
          errors: getErrorForToast(error),
        });
        return submitPromise.current;
      }
    },
  });

  const submit = decorateSubmit(formData.submit, submitPromise);

  /**
   * Extracted form values, without field methods.
   * Including dynamicList data (timeClock section).
   */
  const formValues = useMemo(() => getFieldValues(formData), [formData]);

  return {
    formData,
    formValues,
    submit,
  };
};
